import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

interface AddonsDrawerProps {
  open: boolean;
  onClose: () => void;
  formData: any;
  onChange: (updatedData: any) => void;
  sideItems: any[];
  meals: any[];
  currentMealId?: string;
}

const AddonsDrawer: React.FC<AddonsDrawerProps> = ({
  open,
  onClose,
  formData,
  onChange,
  sideItems,
  meals,
  currentMealId,
}) => {
  const moveSuggested = (index: number, direction: "up" | "down") => {
    const list = [...(formData.suggestedSides || [])];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;
    onChange({ ...formData, suggestedSides: list });
  };

  const movePaired = (index: number, direction: "up" | "down") => {
    const list = [...(formData.pairedMeals || [])];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;
    onChange({ ...formData, pairedMeals: list });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 460 }, p: 3, boxSizing: "border-box" },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Manage Add-ons
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={3}>
        Configure dip/dressing choices, optional sidebar items, and pairing
        recommendations for this meal.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* SECTION 1: Required Side */}
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Required Side (e.g. Dressing)
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Customers must pick exactly one option (radio selector)
            </Typography>
          </Box>
          <Switch
            checked={formData.requiredSide?.enabled || false}
            onChange={(e) =>
              onChange({
                ...formData,
                requiredSide: {
                  ...formData.requiredSide,
                  enabled: e.target.checked,
                  options: formData.requiredSide?.options || [],
                  defaultSideId: formData.requiredSide?.defaultSideId || "",
                },
              })
            }
          />
        </Box>

        {(formData.requiredSide?.enabled || false) && (
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="req-options-label">
                Select Side Options
              </InputLabel>
              <Select
                labelId="req-options-label"
                label="Select Side Options"
                multiple
                value={formData.requiredSide?.options || []}
                onChange={(e) => {
                  const val = e.target.value as string[];
                  onChange({
                    ...formData,
                    requiredSide: {
                      ...formData.requiredSide,
                      options: val,
                      defaultSideId: val.includes(
                        formData.requiredSide?.defaultSideId,
                      )
                        ? formData.requiredSide.defaultSideId
                        : "",
                    },
                  });
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value: any) => {
                      const side = sideItems.find((s) => s._id === value);
                      return side ? side.name : value;
                    })}
                  </Box>
                )}
              >
                {sideItems.map((side) => {
                  const isChecked =
                    (formData.requiredSide?.options || []).indexOf(side._id) >
                    -1;
                  return (
                    <MenuItem key={side._id} value={side._id}>
                      <Checkbox checked={isChecked} size="small" />
                      <ListItemText primary={`${side.name} (₹${side.price})`} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!(formData.requiredSide?.options?.length > 0)}
            >
              <InputLabel id="req-default-label">
                Default Side Option
              </InputLabel>
              <Select
                labelId="req-default-label"
                label="Default Side Option"
                value={formData.requiredSide?.defaultSideId || ""}
                onChange={(e) => {
                  onChange({
                    ...formData,
                    requiredSide: {
                      ...formData.requiredSide,
                      defaultSideId: e.target.value as string,
                    },
                  });
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(formData.requiredSide?.options || []).map((optId: any) => {
                  const side = sideItems.find((s) => s._id === optId);
                  return (
                    <MenuItem key={optId} value={optId}>
                      {side ? side.name : optId}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* SECTION 2: Suggested Sides */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
          Suggested Sides (Optional Upsells)
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          mb={2}
        >
          Surfaced in the cart modal with quantity steppers
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="suggested-label">Select Suggested Sides</InputLabel>
          <Select
            labelId="suggested-label"
            label="Select Suggested Sides"
            multiple
            value={formData.suggestedSides || []}
            onChange={(e) => {
              onChange({
                ...formData,
                suggestedSides: e.target.value as string[],
              });
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value: any) => {
                  const side = sideItems.find((s) => s._id === value);
                  return side ? side.name : value;
                })}
              </Box>
            )}
          >
            {sideItems.map((side) => {
              const isChecked =
                (formData.suggestedSides || []).indexOf(side._id) > -1;
              return (
                <MenuItem key={side._id} value={side._id}>
                  <Checkbox checked={isChecked} size="small" />
                  <ListItemText primary={`${side.name} (₹${side.price})`} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <List dense disablePadding>
          {(formData.suggestedSides || []).map(
            (sideId: string, index: number) => {
              const side = sideItems.find((s) => s._id === sideId);
              if (!side) return null;
              return (
                <ListItem
                  key={sideId}
                  sx={{ bgcolor: "#f8fafc", mb: 1, borderRadius: 2, py: 0.5 }}
                >
                  <ListItemText
                    primary={side.name}
                    secondary={`₹${side.price}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => moveSuggested(index, "up")}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === formData.suggestedSides.length - 1}
                      onClick={() => moveSuggested(index, "down")}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            },
          )}
        </List>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* SECTION 3: Pair it With */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
          Pair it With (Contextual Upsells)
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          mb={2}
        >
          Recommended meal items displayed at bottom of add-to-cart modal
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="paired-label">Select Paired Meals</InputLabel>
          <Select
            labelId="paired-label"
            label="Select Paired Meals"
            multiple
            value={formData.pairedMeals || []}
            onChange={(e) => {
              onChange({
                ...formData,
                pairedMeals: e.target.value as string[],
              });
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value: any) => {
                  const meal = meals.find((m) => m._id === value);
                  return meal ? meal.name : value;
                })}
              </Box>
            )}
          >
            {meals
              .filter((m) => m._id !== currentMealId)
              .map((meal) => {
                const isChecked =
                  (formData.pairedMeals || []).indexOf(meal._id) > -1;
                return (
                  <MenuItem key={meal._id} value={meal._id}>
                    <Checkbox checked={isChecked} size="small" />
                    <ListItemText primary={meal.name} />
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>

        <List dense disablePadding>
          {(formData.pairedMeals || []).map((mealId: string, index: number) => {
            const meal = meals.find((m) => m._id === mealId);
            if (!meal) return null;
            return (
              <ListItem
                key={mealId}
                sx={{ bgcolor: "#f8fafc", mb: 1, borderRadius: 2, py: 0.5 }}
              >
                <ListItemText primary={meal.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => movePaired(index, "up")}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === formData.pairedMeals.length - 1}
                    onClick={() => movePaired(index, "down")}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Button variant="contained" color="secondary" fullWidth onClick={onClose}>
        Done
      </Button>
    </Drawer>
  );
};

export default AddonsDrawer;
